import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";

import { api, type QueueOrder } from "../api";
import { acceptTimer, areaLabel, C, font, itemsSummary, payLabel, pill, slotLabel, useNow } from "../pharmacy/look";

// P1 · Pharmacy order queue: tabs by status with counts, one card per order
// with its accept countdown, oldest first, refreshed every 15 seconds. The
// new-order sound plays on every page (alerts.tsx).

const TABS: { key: string; label: string; match: (o: QueueOrder) => boolean }[] = [
  { key: "new", label: "New", match: (o) => o.status === "placed" },
  { key: "waiting", label: "Waiting on customer", match: (o) => o.status === "awaiting_customer" },
  { key: "confirmed", label: "Confirmed", match: (o) => o.status === "confirmed" },
  { key: "ready", label: "Ready for pickup", match: (o) => o.status === "ready" },
  { key: "delivered", label: "Delivered today", match: (o) => o.status === "delivered" },
];

export default function PharmacyQueue() {
  const [tab, setTab] = useState("new");
  const now = useNow();
  const queue = useQuery({
    queryKey: ["queue"],
    queryFn: () => api<{ orders: QueueOrder[] }>("/pharmacy/orders"),
    refetchInterval: 15_000,
    refetchIntervalInBackground: true,
  });
  const delivered = useQuery({
    queryKey: ["queue", "delivered-today"],
    queryFn: () => api<{ orders: QueueOrder[] }>("/pharmacy/orders?status=delivered&today=1"),
    refetchInterval: 60_000,
  });
  const all = [...(queue.data?.orders ?? []), ...(delivered.data?.orders ?? [])];
  const current = TABS.find((t) => t.key === tab)!;
  const shown = all.filter(current.match);
  const oldestNew = all.find((o) => o.status === "placed")?.orderId;

  return (
    <div style={{ fontFamily: font.sans, color: C.ink, display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: 12 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <h1 style={{ margin: 0, fontSize: 26, lineHeight: "32px", fontWeight: 700, letterSpacing: "-0.015em" }}>Orders</h1>
          <div style={{ fontSize: 13, lineHeight: "18px", color: C.muted }}>Accept new orders within 10 minutes. Oldest first.</div>
        </div>
        <div style={{ fontSize: 13, color: C.muted }}>
          {new Date(now).toLocaleString("en-US", { timeZone: "Asia/Dhaka", weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).replace(/, (\d+:)/, " · $1")}
        </div>
      </div>

      <div role="tablist" aria-label="Order status" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {TABS.map((t) => {
          const selected = t.key === tab;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={selected}
              onClick={() => setTab(t.key)}
              style={{
                height: 44,
                padding: "0 16px",
                borderRadius: 999,
                border: selected ? "none" : `1px solid ${C.line}`,
                background: selected ? C.teal : C.surface,
                color: selected ? C.onTeal : C.ink,
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {t.label} · {all.filter(t.match).length}
            </button>
          );
        })}
      </div>

      {queue.isLoading && <div style={{ color: C.muted }}>Loading orders…</div>}
      {queue.isError && <div style={{ color: C.danger }}>Could not load orders. Retrying…</div>}
      {!queue.isLoading && shown.length === 0 && (
        <div style={{ color: C.muted, fontSize: 15 }}>{tab === "new" ? "No new orders. This page checks every 15 seconds." : "Nothing here right now."}</div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {shown.map((o) => {
          const timer = acceptTimer(o, now);
          const urgent = o.orderId === oldestNew || !!timer?.soon;
          return (
            <div
              key={o.orderId}
              style={{
                background: C.surface,
                borderRadius: 16,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "16px 24px",
                ...(urgent && o.status === "placed" ? { border: `2px solid ${timer?.late ? C.danger : C.teal}`, padding: "15px 19px" } : { border: `1px solid ${C.line}`, padding: "16px 20px" }),
              }}
            >
              <div style={{ flex: "1 1 170px", display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
                <div style={{ fontSize: 16, lineHeight: "22px", fontWeight: 700 }}>{o.orderId}</div>
                <div style={{ fontSize: 13, lineHeight: "18px", fontWeight: 600, color: timer?.late ? C.danger : urgent ? C.ink : C.muted }}>
                  {timer?.text ?? statusLine(o)}
                </div>
              </div>
              <div style={{ flex: "3 1 300px", display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
                <div style={{ fontSize: 15, lineHeight: "22px" }}>{itemsSummary(o.lines)}</div>
                <div style={{ fontSize: 13, lineHeight: "18px", color: C.muted }}>
                  {areaLabel(o.area, o.zone)} · {slotLabel(o.deliverBy)}
                </div>
              </div>
              <div style={{ flex: "2 1 220px", display: "flex", flexWrap: "wrap", gap: 8 }}>
                <span style={{ fontSize: 13, lineHeight: "18px", fontWeight: 600, padding: "4px 10px", borderRadius: 999, background: C.tint }}>
                  {o.hasPrescription ? "Prescription attached" : "No prescription attached"}
                </span>
                <span style={{ fontSize: 13, lineHeight: "18px", fontWeight: 600, padding: "4px 10px", borderRadius: 999, border: `1px solid ${C.line}` }}>
                  {payLabel(o.paymentMethod)}
                </span>
              </div>
              <Link href={`/queue/${o.orderId}`} style={pill("primary")}>
                Open order
              </Link>
            </div>
          );
        })}
      </div>

      <div style={{ background: C.tint, borderRadius: 14, padding: "12px 16px", fontSize: 13, lineHeight: "18px", color: C.body }}>
        Customer phone numbers and full addresses appear only after you confirm an order. Prescription photos are removed 14 days after delivery.
      </div>
    </div>
  );
}

function statusLine(o: QueueOrder): string {
  if (o.status === "delivered" && o.deliveredAt) {
    return `Delivered ${new Date(o.deliveredAt).toLocaleTimeString("en-US", { timeZone: "Asia/Dhaka", hour: "numeric", minute: "2-digit" })}`;
  }
  if (o.status === "awaiting_customer") return "Waiting for the customer";
  if (o.status === "confirmed") return "Pack and seal";
  if (o.status === "ready") return "Waiting for the rider";
  return o.status;
}
